/**
 * The board table shell (generic; docs/design/boards.md).
 *
 *   - a scroll frame that is a labelled, focusable region, so the keyboard
 *     can scroll a table wider than the screen;
 *   - a sticky header and a FROZEN identity column (`th scope="row"`), so a
 *     row's name stays while the columns move;
 *   - the identity cell holds the row's open/close button (aria-expanded,
 *     aria-controls), and a click anywhere else on the row does the same
 *     unless it landed on a control or ended a text selection;
 *   - the open row's panel renders in a DetailRow directly beneath it.
 *
 * It renders what it is given. Which rows exist, their order and the page are
 * the server's.
 */
import React from 'react';
import SortHead from './SortHead';
import DetailRow from './DetailRow';
import type { BoardColumn } from './types';

export interface BoardIdentity<T> {
  label: string;
  help: string;
  sort?: string;
  render: (row: T) => React.ReactNode;
  /** The name the open button speaks ("Open NORTH RIDGE"). */
  name: (row: T) => string;
  /** False = this row has no panel (e.g. an account with no resident); `why` is said instead. */
  openable?: (row: T) => boolean;
  closedWhy?: (row: T) => string;
}

export interface BoardTableProps<T> {
  label: string;
  columns: BoardColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  identity: BoardIdentity<T>;
  openKey: string | null;
  onToggle: (key: string) => void;
  renderDetail: (row: T) => React.ReactNode;
  detailLabel: (row: T) => string;
  sort: string | null;
  dir: 'asc' | 'desc';
  sorts: string[];
  onSort: (col: string, dir: 'asc' | 'desc') => void;
  busy?: boolean;
  rowClass?: (row: T) => string;
}

const detailId = (key: string) => `board-detail-${key}`;

export default function BoardTable<T>({
  label, columns, rows, rowKey, identity, openKey, onToggle, renderDetail, detailLabel, sort, dir, sorts, onSort, busy = false, rowClass,
}: BoardTableProps<T>) {
  const colSpan = columns.length + 1;
  // Header groups (optional): runs of adjacent columns with the same `group` share one labelled cell above them.
  const groups: Array<{ label: string; span: number; first: string }> = [];
  for (const c of columns) {
    const g = c.group || '';
    const last = groups[groups.length - 1];
    if (last && last.label === g) last.span += 1; else groups.push({ label: g, span: 1, first: c.key });
  }
  const grouped = groups.some((g) => g.label);
  const groupStart = (key: string) => groups.some((g) => g.label && g.first === key);
  return (
    <div className="board-scroll" role="region" aria-label={label} tabIndex={0}>
      <table className={`board-table${grouped ? ' board-table--grouped' : ''}`} aria-busy={busy || undefined}>
        <thead>
          {grouped && (
            <tr className="board-grouprow">
              <td className="board-col-id" />
              {groups.map((g) => (g.label
                ? <th key={g.first} scope="colgroup" colSpan={g.span} className="board-grouphead" data-group={g.label}>{g.label}</th>
                : <td key={g.first} colSpan={g.span} />))}
            </tr>
          )}
          <tr>
            <SortHead label={identity.label} col={identity.sort} sort={sort} dir={dir} sorts={sorts} onSort={onSort} help={identity.help} className="board-col-id" />
            {columns.map((c) => (
              <SortHead key={c.key} label={c.label} col={c.sort} sort={sort} dir={dir} sorts={sorts} onSort={onSort} help={c.help} className={[c.numeric ? 'board-num' : '', c.group ? `board-in-group${groupStart(c.key) ? ' board-group-start' : ''}` : ''].filter(Boolean).join(' ') || undefined} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = rowKey(r);
            const open = openKey === key;
            const openable = identity.openable ? identity.openable(r) : true;
            return (
              <React.Fragment key={key}>
                <tr
                  className={`board-row${open ? ' is-open' : ''}${openable ? ' is-openable' : ''}${rowClass ? ` ${rowClass(r)}` : ''}`}
                  onClick={(e) => {
                    if (!openable) return;
                    if ((e.target as HTMLElement).closest('button, a, input, select, textarea, summary')) return;
                    const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
                    if (sel && !sel.isCollapsed && sel.toString().trim()) return;
                    onToggle(key);
                  }}
                >
                  <th scope="row" className="board-col-id">
                    {openable ? (
                      <button
                        type="button"
                        className="board-open"
                        aria-expanded={open}
                        aria-controls={open ? detailId(key) : undefined}
                        aria-label={`${open ? 'Close' : 'Open'} ${identity.name(r)}`}
                        onClick={(e) => { e.stopPropagation(); onToggle(key); }}
                      >
                        <span className="board-caret" aria-hidden="true" />
                        <span className="board-open__name">{identity.render(r)}</span>
                      </button>
                    ) : (
                      <span className="board-closed">
                        <span className="board-open__name">{identity.render(r)}</span>
                        {identity.closedWhy && <span className="board-closed__why">{identity.closedWhy(r)}</span>}
                      </span>
                    )}
                  </th>
                  {columns.map((c) => <td key={c.key} className={[c.numeric ? 'board-num' : '', c.group ? `board-in-group${groupStart(c.key) ? ' board-group-start' : ''}` : ''].filter(Boolean).join(' ') || undefined}>{c.render(r)}</td>)}
                </tr>
                {open && openable && (
                  <DetailRow id={detailId(key)} colSpan={colSpan} label={detailLabel(r)}>{renderDetail(r)}</DetailRow>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
